
"use client";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

const getRoundedTime = () => {
  const now = new Date();
  const minutes = now.getMinutes();
  const roundedMinutes = Math.floor(minutes / 5) * 5;
  now.setMinutes(roundedMinutes);
  return format(now, 'HH:mm');
};

interface TimePickerProps {
  value: string | undefined; // "HH:mm" or undefined
  onChange: (value: string) => void;
  className?: string;
  showSteppers?: boolean;
}

const pad = (num: number) => num.toString().padStart(2, '0');

export function TimePicker({ value, onChange, className, showSteppers = true }: TimePickerProps) {
  // Handle undefined or empty value by defaulting to current time
  const defaultValue = (value && value.trim() !== '') ? value : getRoundedTime();
  const [hour, minute] = defaultValue.split(':').map(Number);
  
  // Ensure we have valid numbers, fallback to current time if parsing fails
  const validHour = isNaN(hour) ? new Date().getHours() : hour;
  const validMinute = isNaN(minute) ? Math.floor(new Date().getMinutes() / 5) * 5 : minute;

  // Normalize minute to nearest multiple of 5 so it matches an available option
  const normalizedMinute = (Math.round(validMinute / 5) * 5) % 60;
  const normalizedHour = Math.min(23, Math.max(0, validHour));

  const handleHourChange = (newHour: string) => {
    onChange(`${pad(parseInt(newHour))}:${pad(normalizedMinute)}`);
  };

  const handleMinuteChange = (newMinute: string) => {
    onChange(`${pad(normalizedHour)}:${pad(parseInt(newMinute))}`);
  };

  const handleAdjustMinutes = (delta: number) => {
    let totalMinutes = normalizedHour * 60 + normalizedMinute + delta;
    if (totalMinutes < 0) {
      totalMinutes = (totalMinutes + 24 * 60) % (24 * 60);
    } else {
      totalMinutes = totalMinutes % (24 * 60);
    }
    const newH = Math.floor(totalMinutes / 60);
    const newM = totalMinutes % 60;
    onChange(`${pad(newH)}:${pad(newM)}`);
  };

  const handleSetToNow = () => {
    onChange(getRoundedTime());
  };

  return (
    <div className={cn("space-y-1", className)}>
      <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
        <Select onValueChange={handleHourChange} value={normalizedHour.toString()}>
          <SelectTrigger className="px-2.5 sm:px-3 text-xs sm:text-sm font-medium h-10 touch-manipulation">
            <SelectValue placeholder="Hour" />
          </SelectTrigger>
          <SelectContent
            position="popper"
            side="bottom"
            align="start"
            sideOffset={4}
            avoidCollisions={true}
            collisionPadding={{ top: 70, bottom: 24, left: 16, right: 16 }}
            className="max-h-48 overflow-y-auto"
            style={{
              maxHeight: "min(var(--radix-select-content-available-height, 12rem), 12rem)",
            }}
          >
            {Array.from({ length: 24 }, (_, i) => (
              <SelectItem key={i} value={i.toString()} className="text-xs sm:text-sm touch-manipulation">
                {pad(i)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select onValueChange={handleMinuteChange} value={normalizedMinute.toString()}>
          <SelectTrigger className="px-2.5 sm:px-3 text-xs sm:text-sm font-medium h-10 touch-manipulation">
            <SelectValue placeholder="Minute" />
          </SelectTrigger>
          <SelectContent
            position="popper"
            side="bottom"
            align="start"
            sideOffset={4}
            avoidCollisions={true}
            collisionPadding={{ top: 70, bottom: 24, left: 16, right: 16 }}
            className="max-h-48 overflow-y-auto"
            style={{
              maxHeight: "min(var(--radix-select-content-available-height, 12rem), 12rem)",
            }}
          >
            {Array.from({ length: 60 / 5 }, (_, i) => {
              const min = i * 5;
              return (
                <SelectItem key={min} value={min.toString()} className="text-xs sm:text-sm touch-manipulation">
                  {pad(min)}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
      {showSteppers && (
        <div className="flex items-center justify-between gap-1 px-0.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleAdjustMinutes(-15)}
            className="h-6 px-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground touch-manipulation"
            aria-label="Subtract 15 minutes"
          >
            -15m
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleSetToNow}
            className="h-6 px-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground touch-manipulation"
            aria-label="Set to current rounded time"
          >
            Now
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleAdjustMinutes(15)}
            className="h-6 px-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground touch-manipulation"
            aria-label="Add 15 minutes"
          >
            +15m
          </Button>
        </div>
      )}
    </div>
  );
}
